import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Button, Card, Collapse, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useMemo } from "react";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  DownloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import type {
  GrandfatheredSiteReport,
  SiteInvoicingReport,
} from "../../services/client";
import { getPartnerInvoicingReportOptions } from "../../services/client/@tanstack/react-query.gen";
import { useAuthenticatedUser } from "../Auth/AuthContext";
import { isViewAsAdmin } from "../../util/viewAsAdmin";
import {
  TIER_FILTERS,
  TIER_COLORS,
  TIER_LABELS,
  isTierKey,
  formatCurrency,
} from "./billingConstants";
import { formatDate } from "../../util/formatDate";

const { Title, Text } = Typography;

function renderTier(tier: string | null) {
  if (!tier) {
    return <Text type="secondary">—</Text>;
  }
  if (!isTierKey(tier)) {
    return <Tag>{tier}</Tag>;
  }
  return <Tag color={TIER_COLORS[tier]}>{TIER_LABELS[tier]}</Tag>;
}

export const BillingTab: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const adminToken = localStorage.getItem("admin-token");
  const viewAsAdmin = isViewAsAdmin();
  const { data, isLoading, error } = useQuery({
    ...getPartnerInvoicingReportOptions({
      path: { partner_id: user?.partner_id ?? "" },
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
    }),
    enabled: !!user?.partner_id && !!adminToken,
  });

  useEffect(() => {
    if (!viewAsAdmin) {
      navigate("/404");
    }
  }, [viewAsAdmin, navigate]);

  const handleDownloadPdf = async () => {
    if (!data) {
      return;
    }
    const { generateBillingPdf } = await import("./generateInvoicePdf");
    generateBillingPdf(data);
  };

  const siteColumns: ColumnsType<SiteInvoicingReport> = useMemo(
    () => [
      {
        title: "Site Name",
        dataIndex: "site_name",
        key: "site_name",
        sorter: (a, b) => a.site_name.localeCompare(b.site_name),
      },
      {
        title: "Activated At",
        dataIndex: "activated_at",
        key: "activated_at",
        sorter: (a, b) => {
          const aDate = a.activated_at[0] ?? "";
          const bDate = b.activated_at[0] ?? "";
          return aDate.localeCompare(bDate);
        },
        render: (dates: string[]) => {
          if (!dates.length) {
            return "—";
          }
          const firstActivation = dates[0];
          return dates.length > 1 ? (
            <Tooltip title={dates.map((d) => formatDate(d)).join(", ")}>
              <span>
                {formatDate(firstActivation)} <InfoCircleOutlined />
              </span>
            </Tooltip>
          ) : (
            formatDate(firstActivation)
          );
        },
      },
      {
        title: "AC Stations",
        dataIndex: "nb_ac_charging_stations",
        key: "nb_ac_charging_stations",
        align: "right",
        sorter: (a, b) => a.nb_ac_charging_stations - b.nb_ac_charging_stations,
      },
      {
        title: "DC Stations",
        dataIndex: "nb_dc_charging_stations",
        key: "nb_dc_charging_stations",
        align: "right",
        sorter: (a, b) => a.nb_dc_charging_stations - b.nb_dc_charging_stations,
      },
      {
        title: "AC Tier",
        dataIndex: "ac_tier",
        key: "ac_tier",
        filters: TIER_FILTERS,
        onFilter: (value, record) => record.ac_tier === value,
        render: renderTier,
      },
      {
        title: "DC Tier",
        dataIndex: "dc_tier",
        key: "dc_tier",
        filters: TIER_FILTERS,
        onFilter: (value, record) => record.dc_tier === value,
        render: renderTier,
      },
      {
        title: "PV Forecasters",
        dataIndex: "nb_pv_forecasters",
        key: "nb_pv_forecasters",
        align: "right",
        sorter: (a, b) => a.nb_pv_forecasters - b.nb_pv_forecasters,
      },
      {
        title: "UCL Forecasters",
        dataIndex: "nb_ucl_forecasters",
        key: "nb_ucl_forecasters",
        align: "right",
        sorter: (a, b) => a.nb_ucl_forecasters - b.nb_ucl_forecasters,
      },
      {
        title: "Variable Horizon",
        dataIndex: "variable_time_horizon_active",
        key: "variable_time_horizon_active",
        align: "center",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) =>
          record.variable_time_horizon_active === value,
        render: (active: boolean) =>
          active ? (
            <CheckCircleFilled style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleFilled style={{ color: "#d9d9d9" }} />
          ),
      },
      {
        title: "OCPP Broker",
        dataIndex: "has_ocpp_config",
        key: "has_ocpp_config",
        align: "center",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) => record.has_ocpp_config === value,
        render: (has: boolean) =>
          has ? (
            <CheckCircleFilled style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleFilled style={{ color: "#d9d9d9" }} />
          ),
      },
      {
        title: "Driver App",
        dataIndex: "has_drivers",
        key: "has_drivers",
        align: "center",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) => record.has_drivers === value,
        render: (has: boolean) =>
          has ? (
            <CheckCircleFilled style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleFilled style={{ color: "#d9d9d9" }} />
          ),
      },
      {
        title: "Monthly Fee",
        dataIndex: "monthly_fee",
        key: "monthly_fee",
        align: "right",
        sorter: (a, b) => parseFloat(a.monthly_fee) - parseFloat(b.monthly_fee),
        render: (v: string) => formatCurrency(v),
      },
      {
        title: "Horizon Addon",
        dataIndex: "variable_time_horizon_addon",
        key: "variable_time_horizon_addon",
        align: "right",
        sorter: (a, b) =>
          parseFloat(a.variable_time_horizon_addon) -
          parseFloat(b.variable_time_horizon_addon),
        render: (v: string) => formatCurrency(v),
      },
      {
        title: "OCPP Broker Fee",
        dataIndex: "ocpp_broker_fee",
        key: "ocpp_broker_fee",
        align: "right",
        sorter: (a, b) =>
          parseFloat(a.ocpp_broker_fee) - parseFloat(b.ocpp_broker_fee),
        render: (v: string) => formatCurrency(v),
      },
      {
        title: "Driver App Fee",
        dataIndex: "driver_app_fee",
        key: "driver_app_fee",
        align: "right",
        sorter: (a, b) =>
          parseFloat(a.driver_app_fee) - parseFloat(b.driver_app_fee),
        render: (v: string) => formatCurrency(v),
      },
      {
        title: "Total",
        dataIndex: "total_monthly_price",
        key: "total_monthly_price",
        align: "right",
        sorter: (a, b) =>
          parseFloat(a.total_monthly_price) - parseFloat(b.total_monthly_price),
        defaultSortOrder: "descend",
        render: (value: string) => <Text strong>{formatCurrency(value)}</Text>,
      },
    ],
    [],
  );

  const grandfatheredColumns: ColumnsType<GrandfatheredSiteReport> = useMemo(
    () => [
      {
        title: "Site Name",
        dataIndex: "site_name",
        key: "site_name",
        sorter: (a, b) => a.site_name.localeCompare(b.site_name),
      },
      {
        title: "Status",
        dataIndex: "active",
        key: "active",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) => record.active === value,
        render: (active: boolean) =>
          active ? (
            <Tag color="green">Active</Tag>
          ) : (
            <Tag color="default">Inactive</Tag>
          ),
      },
      {
        title: "Activated At",
        dataIndex: "activated_at",
        key: "activated_at",
        sorter: (a, b) => {
          const aDate = a.activated_at[0] ?? "";
          const bDate = b.activated_at[0] ?? "";
          return aDate.localeCompare(bDate);
        },
        render: (dates: string[]) => {
          if (!dates.length) {
            return "—";
          }
          const firstActivation = dates[0];
          return dates.length > 1 ? (
            <Tooltip title={dates.map((d) => formatDate(d)).join(", ")}>
              <span>
                {formatDate(firstActivation)} <InfoCircleOutlined />
              </span>
            </Tooltip>
          ) : (
            formatDate(firstActivation)
          );
        },
      },
      {
        title: "Last Deactivated",
        dataIndex: "last_deactivated_at",
        key: "last_deactivated_at",
        sorter: (a, b) =>
          (a.last_deactivated_at ?? "").localeCompare(
            b.last_deactivated_at ?? "",
          ),
        render: (d: string | null) => (d ? formatDate(d) : "—"),
      },
      {
        title: "AC / DC Stations",
        key: "stations",
        sorter: (a, b) =>
          a.nb_ac_charging_stations +
          a.nb_dc_charging_stations -
          (b.nb_ac_charging_stations + b.nb_dc_charging_stations),
        render: (_: unknown, record: GrandfatheredSiteReport) =>
          `${record.nb_ac_charging_stations} / ${record.nb_dc_charging_stations}`,
      },
      {
        title: "AC / DC Tier",
        key: "tiers",
        filters: TIER_FILTERS,
        onFilter: (value, record) =>
          record.ac_tier === value || record.dc_tier === value,
        render: (_: unknown, record: GrandfatheredSiteReport) => (
          <span>
            {renderTier(record.ac_tier)} / {renderTier(record.dc_tier)}
          </span>
        ),
      },
      {
        title: "OCPP Broker",
        dataIndex: "has_ocpp_config",
        key: "has_ocpp_config",
        align: "center",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) => record.has_ocpp_config === value,
        render: (has: boolean) =>
          has ? (
            <CheckCircleFilled style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleFilled style={{ color: "#d9d9d9" }} />
          ),
      },
      {
        title: "Driver App",
        dataIndex: "has_drivers",
        key: "has_drivers",
        align: "center",
        filters: [
          { text: "Active", value: true },
          { text: "Inactive", value: false },
        ],
        onFilter: (value, record) => record.has_drivers === value,
        render: (has: boolean) =>
          has ? (
            <CheckCircleFilled style={{ color: "#52c41a" }} />
          ) : (
            <CloseCircleFilled style={{ color: "#d9d9d9" }} />
          ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <Card>
        <div className="flex items-center justify-between mb-4">
          <div>
            <Title level={3} className="!mb-1">
              {data?.partner_id ?? "Partner"} — Invoicing Report
            </Title>
            {data && (
              <Text className="text-2xl font-semibold">
                Total Monthly Price:{" "}
                <Text strong className="text-2xl">
                  {formatCurrency(data.total_monthly_price)}
                </Text>
              </Text>
            )}
          </div>
          {data && (
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={handleDownloadPdf}
            >
              Download Billing PDF
            </Button>
          )}
        </div>

        {isLoading && <p>Loading invoicing report...</p>}
        {error && <p>Error loading invoicing report: {error.message}</p>}

        {data && (
          <Table<SiteInvoicingReport>
            dataSource={data.sites}
            columns={siteColumns}
            rowKey="site_id"
            pagination={false}
            size="middle"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={16} align="right">
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>{formatCurrency(data.total_monthly_price)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}
      </Card>

      {data?.grandfathered_sites && data.grandfathered_sites.length > 0 && (
        <Card>
          <Collapse
            ghost
            defaultActiveKey={[]}
            items={[
              {
                key: "grandfathered",
                label: (
                  <Text type="secondary" strong>
                    Grandfathered (not billed) —{" "}
                    {data.grandfathered_sites.length} sites
                  </Text>
                ),
                children: (
                  <Table<GrandfatheredSiteReport>
                    dataSource={data.grandfathered_sites}
                    columns={grandfatheredColumns}
                    rowKey="site_id"
                    pagination={false}
                    size="small"
                    className="opacity-75"
                  />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};
